'use client';
import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import HomepageHeader from '@/components/HomepageHeader';

export default function Directory() {
  const [selectedActivity, setSelectedActivity] = useState('');
  const [searchLocation, setSearchLocation] = useState('');

  return (
    <div className="min-h-screen bg-[#F6EFED] page-transition">
      {/* Header Component */}
      <HomepageHeader />

      {/* Hero Section - Same as Homepage */}
      <section className="relative min-h-[720px] flex items-center">
        {/* Background Image/Video */}
        <div className="absolute inset-0">
          <Image
            src="/hero-banner.jpg"
            alt="Women working out in gym"
            fill
            className="object-cover"
            priority
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-[#45394B]/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Find Verified Women-Only Fitness Near You
            </h1>
            <p className="text-lg text-white/90 mb-8 max-w-xl">
              Discover safe, women-only gyms, studios activities customised for your comfort and privacy.
            </p>

            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="flex-1 relative">
                <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#a0969e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Enter postcode or area"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-[#2b262e] placeholder:text-[#a0969e] outline-none focus:ring-2 focus:ring-[#8a7490]"
                />
              </div>
              <select
                value={selectedActivity}
                onChange={(e) => setSelectedActivity(e.target.value)}
                className="px-4 py-4 bg-white rounded-xl text-[#2b262e] outline-none focus:ring-2 focus:ring-[#8a7490] min-w-[200px]"
              >
                <option value="">Select Activity</option>
                <option value="yoga">Yoga</option>
                <option value="pilates">Pilates</option>
                <option value="gym">Gym</option>
                <option value="dance">Dance</option>
              </select>
              <button className="px-8 py-4 bg-[#8a7490] text-white font-medium rounded-xl hover:bg-[#7a647d] transition-colors flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-[280px_1fr_280px] gap-8">
          {/* Left Sidebar - Filters */}
          <aside className="bg-[#faf8f7] rounded-[18px] p-6 h-fit">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-[#2b262e]">Filters</h3>
              <button className="text-xs text-[#8a7490] px-3 py-1 border border-[#8a7490] rounded-full hover:bg-[#8a7490] hover:text-white transition-colors">
                Reset Filter
              </button>
            </div>

            {/* Filter Options */}
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" defaultChecked />
                <span className="text-sm text-[#2b262e]">Verified by FFF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                <span className="text-sm text-[#2b262e]">Women-Only Spaces</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                <span className="text-sm text-[#2b262e]">Women-Led Businesses</span>
              </label>
            </div>

            {/* Session Type */}
            <div className="mt-6 pt-6 border-t border-[#e8e1e6]">
              <h4 className="font-medium text-[#2b262e] mb-4 text-sm">Session Type</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Strength Classes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Yoga</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Pilates</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Gym</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Dance</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Personal Training</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-[#e8e1e6] text-[#8a7490] focus:ring-[#8a7490]" />
                  <span className="text-sm text-[#6e6672]">Swimming</span>
                </label>
              </div>
            </div>

            {/* Business Partnerships */}
            <div className="mt-6 pt-6 border-t border-[#e8e1e6]">
              <h4 className="font-medium text-[#2b262e] mb-3 text-sm">Business Partnerships</h4>
              <p className="text-xs text-[#6e6672] mb-4">Own a women-only fitness space?</p>
              <Link href="/join" className="text-xs text-[#8a7490] font-medium">Join Female Fitness Finder</Link>
              <button className="w-full mt-3 px-4 py-2 bg-[#8a7490] text-white text-sm font-medium rounded-lg hover:bg-[#7a647d] transition-colors flex items-center justify-center gap-1">
                Apply to be listed on FFF
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              <button className="w-full mt-2 text-xs text-[#6e6672] underline hover:text-[#2b262e] transition-colors">
                How Verification Works
              </button>
            </div>
          </aside>

          {/* Center - Listings */}
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-[#2b262e] mb-1">Verified Women-Only Fitness Activities Near You</h2>
              <p className="text-sm text-[#6e6672]">Showing <span className="font-medium text-[#2b262e]">2 spaces</span> near you</p>
            </div>

            {/* Listing Cards */}
            <div className="space-y-6">
              {/* Card 1 */}
              <div className="bg-white rounded-[18px] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="grid md:grid-cols-[200px_1fr] gap-4">
                  <div className="relative h-48 md:h-auto">
                    <Image
                      src="/gym-1.jpg"
                      alt="Empowered Gyms Bromley"
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-[#2b262e] mb-1">Empowered Gyms Bromley Online</h3>
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-4 h-4 text-[#8a7490]" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="text-sm text-[#6e6672]">5.0 (15 reviews)</span>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-[#e8f5e9] text-[#2e7d32] text-xs font-medium rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified by FFF
                      </span>
                    </div>
                    <p className="text-sm text-[#6e6672] mb-4 line-clamp-2">
                      A diverse women-only yoga and mindfulness studio nestled in the heart of Islington. Founded by women, for women - every class... <Link href="#" className="text-[#8a7490] font-medium">Read More</Link>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Strength</span>
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Yoga</span>
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Pilates</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-white border border-[#e8e1e6] text-[#6e6672] text-xs rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        Women-Only
                      </span>
                      <span className="px-3 py-1 bg-white border border-[#e8e1e6] text-[#6e6672] text-xs rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        Women-Led
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6e6672] flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Verified by Female Fitness Finder
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-4">
                      <button className="px-5 py-2 bg-[#8a7490] text-white text-sm font-medium rounded-lg hover:bg-[#7a647d] transition-colors flex items-center gap-1">
                        Send Enquiry
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                      <button className="px-5 py-2 bg-white border border-[#e8e1e6] text-[#6e6672] text-sm font-medium rounded-lg hover:bg-[#f5f2f0] transition-colors flex items-center gap-1">
                        View Details
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-[18px] overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="grid md:grid-cols-[200px_1fr] gap-4">
                  <div className="relative h-48 md:h-auto">
                    <Image
                      src="/gym-2.jpg"
                      alt="Empowered Gyms Bromley"
                      fill
                      className="object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-[#2b262e] mb-1">Empowered Gyms Bromley Online</h3>
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className="w-4 h-4 text-[#8a7490]" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="text-sm text-[#6e6672]">5.0 (15 reviews)</span>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-[#e8f5e9] text-[#2e7d32] text-xs font-medium rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified by FFF
                      </span>
                    </div>
                    <p className="text-sm text-[#6e6672] mb-4 line-clamp-2">
                      A diverse women-only yoga and mindfulness studio nestled in the heart of Islington. Founded by women, for women - every class... <Link href="#" className="text-[#8a7490] font-medium">Read More</Link>
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Strength</span>
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Yoga</span>
                      <span className="px-3 py-1 bg-[#f5f2f0] text-[#6e6672] text-xs rounded-full">Pilates</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-white border border-[#e8e1e6] text-[#6e6672] text-xs rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        Women-Only
                      </span>
                      <span className="px-3 py-1 bg-white border border-[#e8e1e6] text-[#6e6672] text-xs rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        Women-Led
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6e6672] flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        Verified by Female Fitness Finder
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-4">
                      <button className="px-5 py-2 bg-[#8a7490] text-white text-sm font-medium rounded-lg hover:bg-[#7a647d] transition-colors flex items-center gap-1">
                        Send Enquiry
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                      <button className="px-5 py-2 bg-white border border-[#e8e1e6] text-[#6e6672] text-sm font-medium rounded-lg hover:bg-[#f5f2f0] transition-colors flex items-center gap-1">
                        View Details
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Section */}
            <div className="mt-12 bg-white rounded-[18px] p-8">
              <h2 className="text-2xl font-bold text-[#2b262e] mb-6">Why Choose Female Fitness Finder?</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8a7490] rounded-full flex items-center justify-center text-white font-bold">1</div>
                  <div>
                    <h3 className="font-semibold text-[#2b262e] mb-1">Designed for women-first</h3>
                    <p className="text-sm text-[#6e6672]">Connect with women searching for women-only gyms and studios.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8a7490] rounded-full flex items-center justify-center text-white font-bold">2</div>
                  <div>
                    <h3 className="font-semibold text-[#2b262e] mb-1">Independently reviewed</h3>
                    <p className="text-sm text-[#6e6672]">Use our space where women's experiences searching for female-only fitness.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#8a7490] rounded-full flex items-center justify-center text-white font-bold">3</div>
                  <div>
                    <h3 className="font-semibold text-[#2b262e] mb-1">Designed for safety, privacy and comfort</h3>
                    <p className="text-sm text-[#6e6672]">Studios with FFF badges labelled as safe, female-only spaces.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Get Listed */}
          <aside className="space-y-6">
            <div className="bg-[#faf8f7] rounded-[18px] p-6">
              <h3 className="font-semibold text-[#2b262e] mb-4">Get Listed</h3>
              <p className="text-sm text-[#6e6672] mb-4">
                Reach women searching for female-only fitness hubs and studios trusted community.
              </p>
              <button className="w-full px-4 py-2 bg-[#8a7490] text-white text-sm font-medium rounded-lg hover:bg-[#7a647d] transition-colors flex items-center justify-center gap-1 mb-3">
                Join Female Fitness Finder
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              <button className="w-full text-xs text-[#6e6672] underline hover:text-[#2b262e] transition-colors">
                More About Having Benefit
              </button>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#625564] text-white mt-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">F</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-xl text-white">Female Fitness</span>
                  <span className="text-[10px] tracking-[0.2em] text-white/70 uppercase">Finder</span>
                </div>
              </div>
              <p className="text-sm text-white/60 mt-2">Finding female-led fitness, for you</p>
            </div>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                <span>+44 750 753 5943</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/80">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span>tia@femalefitnessfinder.com</span>
              </div>
              <div className="flex items-center gap-4">
                <a href="#" className="text-white/80 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-white/80 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                  </svg>
                </a>
                <a href="#" className="text-white/80 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 mb-8">
            <nav className="flex flex-wrap justify-between items-center gap-4">
              <Link href="/" className="text-sm text-white/80 hover:text-white transition-colors">Home</Link>
              <Link href="/about" className="text-sm text-white/80 hover:text-white transition-colors">About</Link>
              <Link href="/directory" className="text-sm text-white/80 hover:text-white transition-colors">Directory</Link>
              <Link href="/for-women" className="text-sm text-white/80 hover:text-white transition-colors">For Women</Link>
              <Link href="/contact" className="text-sm text-white/80 hover:text-white transition-colors">Contact</Link>
            </nav>
          </div>
          <div className="border-t border-white/20 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/60">
              Copyright © 2026 <span className="text-[#8a7490]">Female Fitness Finder</span>. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link href="/privacy" className="text-xs text-white/60 hover:text-white transition-colors">Privacy Policy</Link>
              <Link href="/terms" className="text-xs text-white/60 hover:text-white transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
